import React, { useState, useEffect } from 'react';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Card, List, Switch, Button, Input, Form, message } from 'antd';
import PropTypes from 'prop-types';
import LinkButton from '../../../../components/link-button';
import { addpro, putpro } from '../../../../api/index';

const { Item } = List;

const UserDetail = ({ history, location }) => {
  const isEditingExistingUser = !!location.state;

  const initialState = isEditingExistingUser
    ? location.state
    : {
        id: '',
        name: {},
        state: 'enabled',
        forChina: true,
        forGlobal: true,
      };

  const [user, setUser] = useState(initialState);
  const [isEditing, setIsEditing] = useState(!isEditingExistingUser);
  const [form] = Form.useForm();
  const [nameKeys, setNameKeys] = useState(Object.keys(initialState.name));

  useEffect(() => {
    form.setFieldsValue(user);
    setNameKeys(Object.keys(user.name));
  }, [user, form]); // 这里添加 form 作为依赖项

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    form.validateFields().then(async values => {
      const updatedName = nameKeys.reduce((acc, key) => {
        const fieldKey = values[`key_${key}`];
        const fieldValue = values[`name_${key}`];
        if (fieldKey) {
          acc[fieldKey] = fieldValue;
        }
        return acc;
      }, {});

      const updatedUser = {
        ...user,
        id: values.id || user.id, // 将表单中的id值赋给user中的id
        name: updatedName,
        state: values.state ? 'enabled' : 'disabled',
        forChina: values.forChina,
        forGlobal: values.forGlobal,
      };

      try {
        if (!isEditingExistingUser) {
          await addpro(updatedUser);
          message.success('用户添加成功');
          history.goBack();
        } else {
          // 在这里添加更新逻辑，如果有更新接口的话
          await putpro(updatedUser);
          message.success('更新成功');
        }
        setUser(updatedUser);
        setIsEditing(false);
        history.goBack();
      } catch (error) {
        message.error('保存失败，请重试');
      }
    });
  };

  const addLanguageField = () => {
    const newLang = `newLang${nameKeys.length + 1}`;
    setNameKeys([...nameKeys, newLang]);
    form.setFieldsValue({
      ...form.getFieldsValue(),
      [`key_${newLang}`]: '',
      [`name_${newLang}`]: '',
    });
  };

  const removeLanguageField = key => {
    setNameKeys(nameKeys.filter(k => k !== key));
    const fields = form.getFieldsValue();
    delete fields[`key_${key}`];
    delete fields[`name_${key}`];
    form.setFieldsValue(fields);
  };

  const renderTitle = (
    <span>
      <LinkButton onClick={() => history.goBack()}>
        <ArrowLeftOutlined style={{ marginRight: 10, fontSize: 20 }} />
      </LinkButton>
      <span>{isEditingExistingUser ? '编辑用户详情' : '添加用户'}</span>
    </span>
  );

  return (
    <Card title={renderTitle}>
      <List className='user-detail'>
        <Form
          form={form}
          layout='vertical'
        >
          <Item>
            <span className='left'>ID：</span>
            <Form.Item
              name='id'
              noStyle
            >
              <Input readOnly={!isEditing} />
            </Form.Item>
          </Item>
          <Item>
            <span className='left'>状态：</span>
            {isEditing ? (
              <Form.Item
                name='state'
                noStyle
                valuePropName='checked'
              >
                <Switch defaultChecked={user.state === 'enabled'} />
              </Form.Item>
            ) : (
              <span>
                <Switch
                  disabled
                  defaultChecked={user.state === 'enabled'}
                />
              </span>
            )}
          </Item>
          {nameKeys.map((key, index) => (
            <Item key={index}>
              <span className='left'>名称：</span>
              {isEditing ? (
                <>
                  <Form.Item
                    name={`key_${key}`}
                    initialValue={key}
                    noStyle
                  >
                    <Input
                      style={{ width: '40%', marginRight: 8 }}
                      placeholder='键'
                    />
                  </Form.Item>
                  <Form.Item
                    name={`name_${key}`}
                    initialValue={user.name[key]}
                    noStyle
                  >
                    <Input
                      style={{ width: '40%', marginRight: 8 }}
                      placeholder='值'
                    />
                  </Form.Item>
                  <Button
                    type='danger'
                    icon={<DeleteOutlined />}
                    onClick={() => removeLanguageField(key)}
                  />
                </>
              ) : (
                <span>{`${key}: ${user.name[key]}`}</span>
              )}
            </Item>
          ))}
          {isEditing && (
            <Item>
              <Button
                type='dashed'
                onClick={addLanguageField}
                icon={<PlusOutlined />}
              >
                添加语言
              </Button>
            </Item>
          )}
          <Item>
            <span className='left'>中国用户：</span>
            {isEditing ? (
              <Form.Item
                name='forChina'
                noStyle
                valuePropName='checked'
              >
                <Switch defaultChecked={user.forChina} />
              </Form.Item>
            ) : (
              <span>
                <Switch
                  disabled
                  defaultChecked={user.forChina}
                />
              </span>
            )}
          </Item>
          <Item>
            <span className='left'>全球用户：</span>
            {isEditing ? (
              <Form.Item
                name='forGlobal'
                noStyle
                valuePropName='checked'
              >
                <Switch defaultChecked={user.forGlobal} />
              </Form.Item>
            ) : (
              <span>
                <Switch
                  disabled
                  defaultChecked={user.forGlobal}
                />
              </span>
            )}
          </Item>
        </Form>
      </List>
      <div style={{ textAlign: 'right' }}>
        {isEditing ? (
          <Button
            type='primary'
            onClick={handleSave}
          >
            保存
          </Button>
        ) : (
          <Button
            type='primary'
            onClick={handleEdit}
          >
            编辑
          </Button>
        )}
      </div>
    </Card>
  );
};

UserDetail.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default UserDetail;
